import './UserHoverCard.scss';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Popover, FloatingPosition } from '@mantine/core';
import { useLazyPopover } from '@srm/ui/popover-anchor';
import { usePortalTarget } from '@srm/ui/provider';
import { isKeyboardNavigation, trackKeyboardNavigation } from '@srm/ui/lib/keyboardFocus';
import Skeleton from '@srm/ui/skeleton';
import Tooltip from '@srm/ui/tooltip';
import { MdMailOutline, MdPhone, MdApartment, MdMeetingRoom, MdArrowRightAlt } from 'react-icons/md';
import { LuUserX, LuPower, LuSun, LuArrowRightLeft, LuBot } from 'react-icons/lu';
import { abookItemTypeLabel, IPersonCard } from '@models/addressbook/IPersonCard';
import { AddressBookService } from '@services/AddressBookService';
import UserAvatar from '@molecules/UserAvatar/UserAvatar';
import UserAvatarPreviewModal, { type IUserAvatarPreview } from '@molecules/UserAvatar/UserAvatarPreviewModal';
import UserAvatarPreviewContext from '@molecules/UserAvatar/UserAvatarPreviewContext';
import { useAvatarMeta } from '@molecules/UserAvatar/useAvatarMeta';
import GroupMembersModal from '@molecules/GroupMembersModal/GroupMembersModal';
import { stopPointerDownBubble } from '@utils/stopPointerDownBubble';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { ConfigService } from '@/configuration/services/configService';

type LoadState = 'idle' | 'loading' | 'error' | 'ready';

const Row: React.FC<{ icon: React.ReactNode; text: React.ReactNode; href?: string }> = ({ icon, text, href }) => (
    <div className="user-hcard__row">
        <span className="user-hcard__row-ic">{icon}</span>
        {href ? (
            <a className="user-hcard__row-link" href={href} onClick={(e) => e.stopPropagation()}>
                {text}
            </a>
        ) : (
            <span>{text}</span>
        )}
    </div>
);

export interface UserHoverCardProps {
    /** Ключ пользователя — без него hover не показывается. */
    userKey?: string;
    /** Имя для подписи, пока данные не загрузились / при ошибке. */
    fallbackName?: string;
    /** Триггер (аватар), оборачиваемый hover-карточкой. */
    children: React.ReactNode;
    position?: FloatingPosition;
    /**
     * Текстовый триггер: anchor течёт как обычный inline-текст (display:inline,
     * line-height наследуется), а не как inline-flex под аватар. Для <UserName>.
     */
    inline?: boolean;
    /** z-index дропдауна — поднять, если карточка открывается поверх другого поповера. */
    zIndex?: number | string;
    /** Дополнительно открывать контакты с клавиатуры и по нажатию. */
    interactive?: boolean;
    /** Карточку не открывать: по нажатию на якорь открыто своё меню (громкость сотрудника в полосе показа). */
    suppressed?: boolean;
}

/**
 * Переиспользуемая hover-карточка элемента адресной книги: при наведении лениво
 * подгружает данные по ключу (GET /addressbook/personCard/{key}). Для людей —
 * ФИО, должность и контакты; для групп (рабочая группа, группа доступа и т. п.) —
 * тип и состав участников. Самодостаточна — не зависит от компонента-потребителя.
 */
const UserHoverCard: React.FC<UserHoverCardProps> = ({
    userKey,
    fallbackName,
    children,
    position = 'top',
    inline = false,
    zIndex,
    interactive = false,
    suppressed = false,
}) => {
    const runtime = useContext(DocumentRuntimeContext);
    // Хост порталов области (`UiProvider.portalTarget`): карточка из слоя поверх страницы открывается в нём, а не в body.
    const portalTarget = usePortalTarget();
    const [card, setCard] = useState<IPersonCard | null>(null);
    const [state, setState] = useState<LoadState>('idle');
    const [opened, setOpened] = useState(false);
    // Своё меню якоря открылось — карточка закрывается и после него сама не возвращается.
    const [wasSuppressed, setWasSuppressed] = useState(suppressed);
    if (suppressed !== wasSuppressed) {
        setWasSuppressed(suppressed);
        if (suppressed) setOpened(false);
    }
    const [membersOpen, setMembersOpen] = useState(false);
    const inheritedPreview = useContext(UserAvatarPreviewContext);
    const [preview, setPreview] = useState<IUserAvatarPreview | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const popoverOpened = opened && !membersOpen && !preview && !suppressed;
    // Popover монтируется с первого открытия: ФИО на странице реестра — десятки, а закрытый Popover (floating-ui,
    // портал, переход) на каждое — заметная часть рендера. Открывается он рендером после монтирования, иначе
    // Mantine не проиграл бы появление и не вызвал бы onOpen.
    const lazyPopover = useLazyPopover<HTMLSpanElement>({ opened: popoverOpened, haspopup: 'dialog' });
    const { anchorRef } = lazyPopover;
    useEffect(() => {
        trackKeyboardNavigation(anchorRef.current?.ownerDocument ?? document);
        return () => clearTimeout(timer.current);
    }, []);

    const show = () => {
        clearTimeout(timer.current);
        setOpened(true);
    };
    const close = () => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            if (
                anchorRef.current?.contains(document.activeElement) ||
                dropdownRef.current?.contains(document.activeElement)
            )
                return;
            setOpened(false);
        }, 120);
    };
    const targetHandlers = {
        onMouseEnter: () => {
            clearTimeout(timer.current);
            timer.current = setTimeout(show, 350);
        },
        onMouseLeave: close,
        // как у подсказок: фокус открывает карточку, только если его перенесла клавиша перехода
        onFocusCapture: () => {
            if (interactive || isKeyboardNavigation()) show();
        },
        onBlurCapture: close,
    };
    const onTargetKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape' && opened) {
            event.stopPropagation();
            clearTimeout(timer.current);
            setOpened(false);
        }
        if (interactive && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            event.stopPropagation();
            show();
        }
    };

    const load = () => {
        if (!userKey || state !== 'idle') return;
        setState('loading');
        AddressBookService.fetchPersonCard(userKey)
            .then((r) => {
                setCard(r.data);
                setState('ready');
            })
            .catch(() => setState('error'));
    };

    const ownPreview = useCallback((next: IUserAvatarPreview) => {
        clearTimeout(timer.current);
        setOpened(false);
        setPreview(next);
    }, []);

    if (runtime.mode === 'supportPreview' || !userKey) return <>{children}</>;

    const name = card?.displayName || fallbackName || '';
    const place = [card?.department, card?.organization].filter(Boolean).join(' · ');
    const isGroup = !!card?.itemType && card.itemType !== 'user';
    const typeLabel = abookItemTypeLabel(card?.itemType);
    const membersHidden = (card?.membersTotal ?? 0) - (card?.members?.length ?? 0);
    // ВРИО: показываем только не истёкший (активный или запланированный); подпись — период/статус.
    const vrio = !isGroup && card?.dummyKey ? vrioNote(card.dummyStartDate, card.dummyEndDate) : null;
    const hasDeputies = !isGroup && (!!card?.assistantKey || (!!card?.dummyKey && vrio !== null));
    // Состояние учётной записи — один чип в шапке по приоритету (Уволен → Деактивирован → В отпуске).
    const accountState = !isGroup && card ? getAccountState(card) : null;
    // Служебная УЗ (интеграционная / робот) — иконка-метка у имени. null — обычный человек.
    const accountKind = !isGroup && card ? getAccountKind(card) : null;
    // Блок «Руководитель» в карточке группы — только по включённому конфиг-флагу (по умолчанию скрыт).
    const showGroupChief = ConfigService.get()?.application.userHoverCard?.showGroupChief ?? false;
    return (
        <UserAvatarPreviewContext value={inheritedPreview ?? ownPreview}>
            {/* Стабильный anchor: Floating UI меряет позицию по нему, а не по аватару —
            масштаб аватара при наведении не сдвигает карточку. Класс is-active держит
            «подъём» аватара, пока карточка открыта (курсор ушёл на неё). Anchor всегда первый
            узел: монтирование Popover рядом не пересоздаёт его (фокус, состояние триггера). */}
            <span
                {...targetHandlers}
                {...lazyPopover.anchorProps}
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                onKeyDown={onTargetKeyDown}
                onClick={
                    interactive
                        ? (event) => {
                              event.stopPropagation();
                              show();
                          }
                        : undefined
                }
                className={clsx('user-hcard__anchor', inline && 'user-hcard__anchor--inline', opened && 'is-active')}
            >
                {children}
            </span>
            {lazyPopover.mounted && (
                <Popover
                    /* Управляемый hover по дереву React: вложенные порталы остаются частью карточки.
                     * Нативный useHover Mantine 9.7 закрывал родителя при переходе во вложенную карточку/фото. */
                    {...lazyPopover.popoverProps}
                    width={300}
                    shadow="md"
                    radius="md"
                    onChange={setOpened}
                    closeOnEscape={false}
                    position={position}
                    zIndex={zIndex}
                    portalProps={portalTarget ? { target: portalTarget } : undefined}
                    onOpen={load}
                >
                    {lazyPopover.popoverTarget}
                    {/* React-события всплывают по дереву компонентов (не DOM!), поэтому клик внутри
                карточки — хоть она и в портале — доходит до родителя анкера. В гриде папки ячейка
                ФИО обёрнута в <Link> на документ: без барьера клик по зуму аватара (и любой клик
                внутри лайтбокс-модалки, которая в дереве — потомок дропдауна) проваливается в
                навигацию строки. Карточка самодостаточна — её клики не управляют строкой под ней. */}
                    <Popover.Dropdown
                        /* Барьер pointer-down: клик внутри карточки (напр. зум аватара) не должен
                   закрывать попап/меню под ней через их нативный useClickOutside. */
                        ref={(node) => {
                            dropdownRef.current = node;
                            return stopPointerDownBubble(node);
                        }}
                        onMouseEnter={() => clearTimeout(timer.current)}
                        onMouseLeave={close}
                        onFocusCapture={show}
                        onBlurCapture={close}
                        onKeyDown={(event: React.KeyboardEvent) => {
                            if (event.key === 'Escape') {
                                event.stopPropagation();
                                setOpened(false);
                            }
                        }}
                        className={clsx('user-hcard', isGroup ? 'user-hcard--group' : 'user-hcard--person')}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="user-hcard__scroll">
                            {state === 'loading' && (
                                <div className="user-hcard__head" aria-hidden="true">
                                    <Skeleton width={44} height={44} radius={isGroup ? 'md' : 'xl'} />
                                    <div className="user-hcard__id">
                                        <Skeleton height={15} width={132} radius="sm" />
                                        <Skeleton height={11} width={92} radius="sm" mt={7} />
                                    </div>
                                </div>
                            )}
                            {state === 'error' && (
                                <div className="user-hcard__name">{fallbackName || 'Не удалось загрузить'}</div>
                            )}
                            {state === 'ready' && card && (
                                <>
                                    <div className="user-hcard__head">
                                        <UserAvatar
                                            userKey={userKey}
                                            name={name}
                                            hasImage={card?.hasImage}
                                            photoVersion={card?.photoVersion}
                                            type={card?.itemType}
                                            size={44}
                                            radius={isGroup ? 'md' : undefined}
                                            zoomable
                                        />
                                        <div className="user-hcard__id">
                                            <div className="user-hcard__name-row">
                                                <span className="user-hcard__name">{name}</span>
                                                {accountKind && (
                                                    <Tooltip
                                                        background="black"
                                                        wrapTarget={false}
                                                        interactive={false}
                                                        position="top"
                                                        openDelay={200}
                                                        label={accountKind.label}
                                                    >
                                                        <span
                                                            className={clsx(
                                                                'user-hcard__uz',
                                                                `user-hcard__uz--${accountKind.kind}`,
                                                            )}
                                                            role="img"
                                                            aria-label={accountKind.label}
                                                        >
                                                            {accountKind.icon}
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            {isGroup && typeLabel && (
                                                <div className="user-hcard__type">{typeLabel}</div>
                                            )}
                                            {!isGroup && card.post && (
                                                <div className="user-hcard__post">{card.post}</div>
                                            )}
                                            {accountState && (
                                                <span
                                                    className={clsx(
                                                        'user-hcard__state',
                                                        `user-hcard__state--${accountState.kind}`,
                                                    )}
                                                >
                                                    {accountState.icon}
                                                    {accountState.label}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {isGroup && showGroupChief && card.chiefKey && (
                                        <div className="user-hcard__rows user-hcard__rows--people">
                                            <div className="user-hcard__members-label">Руководитель</div>
                                            <PersonLine
                                                zIndex={zIndex}
                                                userKey={card.chiefKey}
                                                name={card.chiefName}
                                                sub={card.chiefPost}
                                                lead
                                            />
                                        </div>
                                    )}
                                    {isGroup && (card.members?.length ?? 0) > 0 && (
                                        <div className="user-hcard__rows user-hcard__rows--people">
                                            <div className="user-hcard__members-label">
                                                Участники
                                                {card.membersTotal != null && (
                                                    <span className="user-hcard__members-count">
                                                        {' · '}
                                                        {card.membersTotal.toLocaleString('ru-RU')}
                                                    </span>
                                                )}
                                            </div>
                                            {/* саморекурсия: hover по участнику открывает его карточку */}
                                            {card.members!.map((member, i) => (
                                                <PersonLine
                                                    key={i}
                                                    userKey={member.key}
                                                    name={member.name}
                                                    zIndex={zIndex}
                                                />
                                            ))}
                                            {membersHidden > 0 && (
                                                <button
                                                    type="button"
                                                    className="user-hcard__show-all"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // иначе якорь держит is-active под окном участников, а после него
                                                        // карточка открылась бы снова
                                                        setOpened(false);
                                                        setMembersOpen(true);
                                                    }}
                                                >
                                                    Показать всех {card.membersTotal?.toLocaleString('ru-RU')}
                                                    <MdArrowRightAlt
                                                        className="user-hcard__show-all-arr"
                                                        size={16}
                                                        aria-hidden
                                                    />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {isGroup && (card.members?.length ?? 0) === 0 && (
                                        <div className="user-hcard__rows">
                                            <div className="user-hcard__members-label">Участники не заданы</div>
                                        </div>
                                    )}
                                    {!isGroup && (place || card.email || card.phone || card.room) && (
                                        <div className="user-hcard__rows">
                                            {place && <Row icon={<MdApartment size={15} />} text={place} />}
                                            {card.email && (
                                                <Row
                                                    icon={<MdMailOutline size={15} />}
                                                    text={card.email}
                                                    href={`mailto:${card.email}`}
                                                />
                                            )}
                                            {card.phone && (
                                                <Row
                                                    icon={<MdPhone size={15} />}
                                                    text={card.phone}
                                                    href={`tel:${card.phone}`}
                                                />
                                            )}
                                            {card.room && (
                                                <Row icon={<MdMeetingRoom size={15} />} text={`Кабинет ${card.room}`} />
                                            )}
                                        </div>
                                    )}
                                    {hasDeputies && (
                                        <div className="user-hcard__rows user-hcard__rows--people">
                                            <div className="user-hcard__members-label">Заместители</div>
                                            {card.assistantKey && (
                                                <PersonLine
                                                    zIndex={zIndex}
                                                    userKey={card.assistantKey}
                                                    name={card.assistantName}
                                                    badge={
                                                        <span className="user-hcard__badge user-hcard__badge--perm">
                                                            Уполномоченный
                                                        </span>
                                                    }
                                                />
                                            )}
                                            {card.dummyKey && vrio !== null && (
                                                <PersonLine
                                                    zIndex={zIndex}
                                                    userKey={card.dummyKey}
                                                    name={card.dummyName}
                                                    sub={vrio}
                                                    badge={
                                                        <span className="user-hcard__badge user-hcard__badge--vrio">
                                                            ВРИО
                                                        </span>
                                                    }
                                                />
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </Popover.Dropdown>
                </Popover>
            )}
            {preview && <UserAvatarPreviewModal {...preview} onClose={() => setPreview(null)} />}
            {isGroup && userKey && (
                <GroupMembersModal
                    opened={membersOpen}
                    onClose={() => setMembersOpen(false)}
                    groupKey={userKey}
                    groupName={name}
                    groupType={card?.itemType}
                    hasImage={card?.hasImage}
                    photoVersion={card?.photoVersion}
                    totalHint={card?.membersTotal}
                />
            )}
        </UserAvatarPreviewContext>
    );
};

/**
 * Строка-персона в карточке (руководитель / участник / заместитель): аватар + имя как
 * триггер вложенной hover-карточки (+ опц. подпись под именем и бейдж справа).
 */
function PersonLine({
    userKey,
    name,
    sub,
    lead,
    badge,
    zIndex,
}: {
    userKey?: string;
    name?: string;
    /** мелкая строка под именем (напр. период ВРИО) */
    sub?: React.ReactNode;
    /** строка руководителя — подсветить бирюзовым тоном */
    lead?: boolean;
    /** бейдж справа (напр. «Уполномоченный» / «ВРИО») */
    badge?: React.ReactNode;
    /** z-index родительской карточки — вложенная открывается на том же слое, а не под ней */
    zIndex?: number | string;
}) {
    // мета аватара батчится (один POST на все строки); фото грузится только при hasImage
    const meta = useAvatarMeta(userKey);
    return (
        <div className={clsx('user-hcard__pline', lead && 'user-hcard__pline--lead')}>
            <UserAvatar
                userKey={userKey}
                name={name || ''}
                hasImage={meta?.hasImage ?? false}
                photoVersion={meta?.photoVersion ?? undefined}
                type="user"
                size={lead ? 26 : 22}
            />
            <div className="user-hcard__pline-who">
                {userKey ? (
                    <UserHoverCard
                        userKey={userKey}
                        fallbackName={name ?? undefined}
                        position="right"
                        zIndex={zIndex}
                        inline
                    >
                        <span className="user-hcard__member-name">{name}</span>
                    </UserHoverCard>
                ) : (
                    <span className="user-hcard__member-name">{name}</span>
                )}
                {sub && <div className="user-hcard__pline-meta">{sub}</div>}
            </div>
            {badge}
        </div>
    );
}

/**
 * Подпись для ВРИО по срокам действия. null — срок истёк (не показываем).
 * Активность: start ≤ сейчас ≤ end (или end не задан) — как на бэке (UserInfoManager).
 */
function vrioNote(start?: string, end?: string): string | null {
    const now = Date.now();
    const s = start ? new Date(start).getTime() : null;
    const e = end ? new Date(end).getTime() : null;
    if (e !== null && e < now) return null;
    const fmt = (ms: number) =>
        new Date(ms).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const active = (s === null || s <= now) && (e === null || e >= now);
    if (active) return e !== null ? `активен · до ${fmt(e)}` : 'активен';
    return s !== null ? `с ${fmt(s)}` : null;
}

/**
 * Состояние учётной записи для чипа в шапке — один по приоритету:
 * Уволен → Деактивирован → В отпуске. null — активен (чип не показываем).
 * Locked показываем как «В отпуске» (тот же флаг зовётся «Заблокирован» в панели учёток).
 */
function getAccountState(card: IPersonCard): { kind: string; label: string; icon: React.ReactNode } | null {
    if (card.isDismissed) return { kind: 'dismissed', label: 'Уволен', icon: <LuUserX size={13} aria-hidden /> };
    if (card.isDeactivated)
        return { kind: 'deactivated', label: 'Деактивирован', icon: <LuPower size={13} aria-hidden /> };
    if (card.isLocked) return { kind: 'vacation', label: 'В отпуске', icon: <LuSun size={13} aria-hidden /> };
    return null;
}

/**
 * Тип служебной учётной записи для иконки-метки у имени (интеграционная / робот).
 * null — обычный человек: метку не показываем. Данные — isIntegration / isUIAutomation.
 */
function getAccountKind(card: IPersonCard): { kind: string; label: string; icon: React.ReactNode } | null {
    if (card.isIntegration)
        return {
            kind: 'int',
            label: 'Интеграционная учётная запись',
            icon: <LuArrowRightLeft size={13} aria-hidden />,
        };
    if (card.isUIAutomation)
        return { kind: 'rpa', label: 'Роботизированная учётная запись', icon: <LuBot size={13} aria-hidden /> };
    return null;
}

export default UserHoverCard;
